/**
 * Multi-Agent Node Architecture Types
 * 
 * This module defines the core types for the node-based agent system
 * where each agent has focused responsibilities, specific tools, and
 * self-managed transitions with guardrails.
 */

import { FunctionDeclaration, LiveConnectConfig, Modality } from "@google/genai";

/**
 * Supported languages in the system
 */
export type SupportedLanguage = 'ar' | 'en' | 'ur';

/**
 * Voice gender options
 */
export type VoiceGender = 'male' | 'female';

/**
 * Agent transition target - where an agent can route to
 */
export interface TransitionTarget {
  agentId: string;
  condition?: string; // Human-readable condition description
}

/**
 * Data that can be passed between agents during transitions
 */
export interface TransitionData {
  language?: SupportedLanguage;
  voiceGender?: VoiceGender;
  userData?: {
    username: string;
    mobileNumber: string;
  };
  userLocation?: {
    lat: number;
    lng: number;
    address?: string;
  };
  // Flow-specific data
  reportData?: any;
  selectedVehicle?: any;
  renewalResult?: any;
  accidentData?: any;
  accidentResult?: any;
  licenseData?: any;
  searchResults?: any;
  // Prefilled report number from lost report flow
  prefilledReportNumber?: string;
  // Pending question from router redirect
  pendingQuestion?: string;
  // Image analysis from accident photos (passed to next agent)
  imageAnalysis?: string;
  // Number of images uploaded in accident flow (for tracking)
  uploadedImageCount?: number;
  // License image data for lost report flow (stored before sending to Gemini)
  licenseImageData?: {
    mimeType: string;
    data: string; // base64 data
  };
  // Custom data for specific flows
  customData?: Record<string, any>;
}

/**
 * A single message in conversation history
 */
export interface ConversationMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  agentId?: string; // Which agent generated this message
}

/**
 * Conversation history carried between agents
 */
export interface ConversationHistory {
  messages: ConversationMessage[];
  summary?: string; // Optional summary for long conversations
  maxMessages?: number; // Limit history size
}

/**
 * Context passed to agents - includes history and transition data
 */
export interface AgentContext {
  conversationHistory: ConversationHistory;
  transitionData: TransitionData;
  previousAgentId?: string;
  transitionReason?: string;
}

/**
 * Guardrail definition for validating transitions
 */
export interface Guardrail {
  id: string;
  description: string;
  /**
   * Validation function that checks if the guardrail condition is met
   * @param context Current agent context
   * @returns true if guardrail passes, false or error message if fails
   */
  validate: (context: AgentContext) => boolean | string;
}

/**
 * Entry guardrails - conditions that must be met to enter an agent
 */
export interface EntryGuardrails {
  required: Guardrail[];
  optional?: Guardrail[]; // Warnings but not blockers
}

/**
 * Exit guardrails - conditions that must be met to leave an agent
 */
export interface ExitGuardrails {
  /**
   * Map of exit conditions to target agents
   * Key is the condition name (e.g., "language_detected", "option_selected")
   * Value is the target agent and validation
   */
  transitions: Map<string, {
    target: TransitionTarget;
    guardrails: Guardrail[];
  }>;
  /**
   * Default fallback if no transition matches
   */
  fallback?: TransitionTarget;
}

/**
 * Tool call result from an agent
 */
export interface ToolCallResult {
  toolName: string;
  args: Record<string, any>;
  result: any;
  triggersTransition?: boolean;
  transitionTo?: string;
  transitionData?: Partial<TransitionData>;
}

/**
 * Agent node definition - the core building block
 */
export interface AgentNode {
  /**
   * Unique identifier for this agent
   */
  id: string;
  
  /**
   * Human-readable name
   */
  name: string;
  
  /**
   * Description of what this agent does
   */
  description: string;
  
  /**
   * Focused system instruction for this agent
   * Only used in LEGACY MODE - in ephemeral token mode, this comes from backend
   * @deprecated Use backend configuration in ephemeral token mode
   */
  systemInstruction?: string;
  
  /**
   * Tools available to this agent
   * Only used in LEGACY MODE - in ephemeral token mode, this comes from backend
   * @deprecated Use backend configuration in ephemeral token mode
   */
  tools?: FunctionDeclaration[];
  
  /**
   * Entry guardrails - what conditions must be met to activate this agent
   */
  entryGuardrails: EntryGuardrails;
  
  /**
   * Exit guardrails - when and where this agent can transition
   */
  exitGuardrails: ExitGuardrails;
  
  /**
   * Response modality configuration
   */
  responseModality?: Modality[];
  
  /**
   * Voice configuration for this agent (can differ per agent if needed)
   */
  voiceConfig?: {
    voiceName: string;
  };
  
  /**
   * Initial prompt sent to the AI when agent activates
   * This triggers the AI to speak first (greeting, menu, instructions, etc.)
   * Without this, the AI waits for user input before responding.
   */
  initialPrompt?: string;
  
  /**
   * Handler for tool calls specific to this agent
   * Returns updated context and potential transition
   */
  handleToolCall?: (
    toolName: string,
    args: Record<string, any>,
    context: AgentContext
  ) => Promise<ToolCallResult>;
  
  /**
   * Optional callback when agent is activated
   */
  onActivate?: (context: AgentContext) => Promise<void>;
  
  /**
   * Optional callback when agent is deactivated
   */
  onDeactivate?: (context: AgentContext) => Promise<void>;
}

/**
 * Agent transition event
 */
export interface AgentTransitionEvent {
  fromAgentId: string;
  toAgentId: string;
  reason: string;
  context: AgentContext;
  timestamp: Date;
}

/**
 * Agent manager state
 */
export interface AgentManagerState {
  currentAgentId: string | null;
  context: AgentContext;
  isConnected: boolean;
  isTransitioning: boolean;
  transitionHistory: AgentTransitionEvent[];
}

/**
 * Configuration for creating an agent's LiveConnectConfig
 */
export interface AgentLiveConfig {
  systemInstruction: string;
  tools: FunctionDeclaration[];
  voiceGender: VoiceGender;
  responseModality?: Modality[];
}

/**
 * Build LiveConnectConfig from agent configuration
 */
export function buildLiveConfig(config: AgentLiveConfig): LiveConnectConfig {
  const voiceName = config.voiceGender === 'female' ? 'Aoede' : 'Charon';
  
  return {
    responseModalities: config.responseModality || [Modality.AUDIO],
    speechConfig: {
      voiceConfig: { prebuiltVoiceConfig: { voiceName } },
    },
    systemInstruction: {
      parts: [{ text: config.systemInstruction }],
    },
    tools: [{ functionDeclarations: config.tools }],
  };
}

/**
 * Create empty context for initial agent activation
 */
export function createInitialContext(
  userData?: TransitionData['userData'],
  voiceGender: VoiceGender = 'male'
): AgentContext {
  return {
    conversationHistory: {
      messages: [],
      maxMessages: 50,
    },
    transitionData: {
      voiceGender,
      userData,
    },
  };
}

/**
 * Add a message to conversation history
 */
export function addToHistory(
  history: ConversationHistory,
  message: Omit<ConversationMessage, 'timestamp'>
): ConversationHistory {
  const newMessage: ConversationMessage = {
    ...message,
    timestamp: new Date(),
  };
  
  const messages = [...history.messages, newMessage];
  
  // Trim if exceeds max
  const maxMessages = history.maxMessages || 50;
  const trimmedMessages = messages.length > maxMessages
    ? messages.slice(-maxMessages)
    : messages;
  
  return {
    ...history,
    messages: trimmedMessages,
  };
}

/**
 * Get recent history as text for context injection
 */
export function getHistoryText(
  history: ConversationHistory,
  lastN: number = 10
): string {
  const recentMessages = history.messages.slice(-lastN);
  
  return recentMessages
    .map(msg => `${msg.role}: ${msg.content}`)
    .join('\n');
}

